import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchUsers, fetchUser, updateUserStatus, updateUser } from '../services/users';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [editRole, setEditRole] = useState('user');
  const [editStatus, setEditStatus] = useState('active');
  const [savingUser, setSavingUser] = useState(false);
  const itemsPerPage = 10;

  // Generate avatar URL from user name
  const getAvatarUrl = (name) => {
    const encodedName = encodeURIComponent(name);
    return `https://ui-avatars.com/api/?name=${encodedName}&background=1a5d1a&color=fff`;
  };

  // Transform backend user data to frontend format
  const transformUser = (user) => {
    return {
      id: user.id,
      name: user.name || 'Unknown User',
      email: user.email,
      phone: user.phone || 'N/A',
      role: user.role || 'user',
      signupDate: user.created_at,
      status: user.status === 'active' ? 'Active' : 'Inactive',
      orders: user.orders_count || 0,
      avatar: getAvatarUrl(user.name || user.email),
    };
  };

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchUsers({
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm || undefined,
        });

        // Laravel pagination response structure
        const transformedUsers = response.data.map(transformUser);
        setUsers(transformedUsers);
        setTotalPages(response.last_page || 1);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.response?.data?.message || 'Failed to load users');
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const backendStatus = newStatus.toLowerCase();
      await updateUserStatus(userId, backendStatus);
      setUsers(users.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      ));
    } catch (err) {
      console.error('Error updating user status:', err);
      alert(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    const roleValue = newRole === 'Admin' ? 'admin' : 'user';
    try {
      await updateUser(userId, { role: roleValue });
      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: roleValue } : user
      ));
    } catch (err) {
      console.error('Error updating user role:', err);
      alert(err.response?.data?.message || 'Failed to update user role');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewUser = async (userId) => {
    setLoadingUserDetails(true);
    setShowModal(true);
    try {
      const userData = await fetchUser(userId);
      const transformed = {
        ...transformUser(userData),
        emailVerifiedAt: userData.email_verified_at,
        phoneVerifiedAt: userData.phone_verified_at,
        createdAt: userData.created_at,
        updatedAt: userData.updated_at,
      };
      setSelectedUser(transformed);
      setEditRole(transformed.role || 'user');
      setEditStatus(transformed.status === 'Active' ? 'active' : 'inactive');
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.message || 'Failed to load user details');
      setShowModal(false);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setSavingUser(false);
  };

  const handleSaveUserChanges = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      await updateUser(selectedUser.id, { role: editRole, status: editStatus });
      const updated = {
        ...selectedUser,
        role: editRole,
        status: editStatus === 'active' ? 'Active' : 'Inactive',
      };
      setSelectedUser(updated);
      setUsers(users.map(u => (u.id === selectedUser.id ? { ...u, role: editRole, status: updated.status } : u)));
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  return (
    <div className="admin-users-page">
      <AdminPageTitle title="Users" />
      <div className="page-header">
        <h1>Users Management</h1>
      </div>

      <div className="users-filters">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Signup Date</th>
                  <th>Orders</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <img src={user.avatar} alt={user.name} className="user-avatar" />
                          <span>{user.name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>
                        <select
                          value={user.role === 'admin' ? 'Admin' : 'User'}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className={`role-select ${user.role === 'admin' ? 'role-admin' : 'role-user'}`}
                        >
                          <option value="User">User</option>
                          <option value="Admin">Admin</option>
                        </select>
                      </td>
                      <td>{formatDate(user.signupDate)}</td>
                      <td>{user.orders}</td>
                      <td>
                        <select
                          value={user.status}
                          onChange={(e) => handleStatusChange(user.id, e.target.value)}
                          className={`status-select ${user.status === 'Active' ? 'status-active' : 'status-inactive'}`}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </td>
                      <td>
                        <button 
                          className="action-btn view-btn"
                          onClick={() => handleViewUser(user.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* User Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            {loadingUserDetails ? (
              <div className="modal-loading">Loading user details...</div>
            ) : selectedUser ? (
              <div className="user-details">
                <div className="user-details-header">
                  <img src={selectedUser.avatar} alt={selectedUser.name} className="user-details-avatar" />
                  <div>
                    <h3>{selectedUser.name}</h3>
                    <p className="user-details-email">{selectedUser.email}</p>
                  </div>
                </div>

                <div className="user-details-grid">
                  <div className="detail-item">
                    <label>User ID</label>
                    <span>{selectedUser.id}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Email</label>
                    <span>{selectedUser.email}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Phone</label>
                    <span>{selectedUser.phone}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Role</label>
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className={`role-select ${editRole === 'admin' ? 'role-admin' : 'role-user'}`}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  
                  <div className="detail-item">
                    <label>Status</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className={`status-select ${editStatus === 'active' ? 'status-active' : 'status-inactive'}`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  
                  <div className="detail-item">
                    <label>Total Orders</label>
                    <span>{selectedUser.orders}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Email Verified</label>
                    <span>{selectedUser.emailVerifiedAt ? formatDateTime(selectedUser.emailVerifiedAt) : 'Not Verified'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Phone Verified</label>
                    <span>{selectedUser.phoneVerifiedAt ? formatDateTime(selectedUser.phoneVerifiedAt) : 'Not Verified'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Signup Date</label>
                    <span>{formatDateTime(selectedUser.createdAt)}</span>
                  </div>
                  
                  <div className="detail-item">
                    <label>Last Updated</label>
                    <span>{formatDateTime(selectedUser.updatedAt)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="modal-error">Failed to load user details</div>
            )}
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>Close</button>
              {selectedUser && (
                <button
                  className="btn-primary"
                  onClick={handleSaveUserChanges}
                  disabled={savingUser}
                >
                  {savingUser ? 'Saving...' : 'Save changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
